import { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { FreshLeadStatus } from '../../enums/FreshLeadStatus'
import FreshLeadInterface from '../../interfaces/Cook/FreshLeadInterface'
import SkillAssessmentRatingTable from '../Common/SkillAssessmentRatingTable'
import { CookService } from "../../services/CookServices"
import SkillAssessmentHistoryInterface, { PreviousAssessment } from '../../interfaces/Cook/SkillAssessmentHistoryInterface'
import { formatDateTime } from '../../utils/DateTimeUtils'

interface InputProps {
    cookDetail: FreshLeadInterface
    refreshToken?: number
}

interface AssessmentDataInterface {
    moduleId: number;
    moduleName: string;
    moduleTopics: {
        attempted: boolean;
        completed: boolean;
        maximumScore: number;
        topicProgressId: number;
        percentAttempted: number;
        topicId: number;
        topicName: string;
        totalScore: number;
    }[];
}

const cookService = new CookService()

function groupTopicsByModule(assessmentData: any): AssessmentDataInterface[] {
    const moduleMap = new Map()
    assessmentData.assessmentTopicProgess.forEach((topicProgress: any) => {
        const { id, topic, completed, attempted, totalScore, maximumScore, percentAttempted } = topicProgress
        const { module } = topic
        if (!moduleMap.has(module.id)) {
            moduleMap.set(module.id, { moduleId: module.id, moduleName: module.localEn, moduleTopics: [] })
        }
        moduleMap.get(module.id).moduleTopics.push({
            topicId: topic.id,
            topicName: topic.localEn,
            topicProgressId: id,
            completed,
            attempted,
            totalScore,
            maximumScore,
            percentAttempted,
        })
    })
    return Array.from(moduleMap.values())
}

const MODULE_ACCENT: Record<string, string> = {
    default: "#283593",
}

const SkillAssessmentTable = ({ cookDetail, refreshToken }: InputProps) => {
    const [assessmentData, setAssessmentData] = useState<AssessmentDataInterface[]>([])
    const [latestTakenAt, setLatestTakenAt] = useState<string | Date | null>(null)
    const [previousAssessments, setPreviousAssessments] = useState<PreviousAssessment[]>([])

    useEffect(() => {
        if (!cookDetail?.cookId) return
        cookService.getTopicProgress(cookDetail.cookId, { type: 'SKILLASSESSMENT' }).then((res) => {
            setAssessmentData(groupTopicsByModule(res))
        })
        cookService.getSkillAssessmentHistory(cookDetail.cookId).then((res: SkillAssessmentHistoryInterface) => {
            if (!res) return
            setLatestTakenAt(res.latestTakenAt ?? null)
            // Support both array and legacy single previousAssessment
            const list: PreviousAssessment[] = [
                ...(res.previousAssessments ?? (res.previousAssessment ? [res.previousAssessment] : [])),
            ]
            // Take at most 3, reverse so index-0 = oldest (displayed leftmost)
            setPreviousAssessments(list.slice(0, 3).reverse())
        })
    }, [cookDetail?.cookId, refreshToken])

    if (
        cookDetail.onboardingStatus === FreshLeadStatus.PROFILE ||
        cookDetail.onboardingStatus === FreshLeadStatus.FRESH_LEAD
    ) {
        return null
    }

    return (
        <Box mt={1.5}>
            {latestTakenAt && (
                <Typography sx={{ fontSize: "0.74rem", color: "#455A64", fontWeight: 600, mb: 1 }}>
                    Latest assessment taken: {formatDateTime(latestTakenAt)}
                </Typography>
            )}

            {assessmentData.map((eachModule) => {
                const accent = MODULE_ACCENT[eachModule.moduleName] ?? MODULE_ACCENT.default
                return (
                    <Box
                        key={eachModule.moduleId}
                        sx={{
                            bgcolor: "#fff",
                            border: "1px solid rgb(226,230,233)",
                            borderTop: `3px solid ${accent}`,
                            borderRadius: "12px",
                            boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
                            p: "12px 14px",
                            mb: 1.5,
                        }}
                    >
                        {/* Card title bar */}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.75}
                            mb={2}
                            pb={1}
                            sx={{ borderBottom: "1.5px dashed rgb(210,214,219)", bgcolor: `${accent}14`, mx: "-14px", px: "14px", pt: "6px", mt: "-12px", borderRadius: "9px 9px 0 0" }}
                        >
                            <span style={{ fontSize: 13 }}>🎯</span>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: "0.6px", filter: "brightness(0.7)" }}>
                                {eachModule.moduleName}
                            </Typography>
                        </Box>

                        <SkillAssessmentRatingTable
                            cookDetail={cookDetail}
                            moduleData={eachModule}
                            previousAssessments={previousAssessments}
                        />
                    </Box>
                )
            })}
        </Box>
    )
}

export default SkillAssessmentTable
